import React from 'react'
import { RenderDialogProps } from '../../../legacy-design-system/action/confirmation-action'
import {
  ConfirmationRoleAction,
  ConfirmationAction
} from '../../../legacy-design-system/action'
import {
  ModifyVulnerabilityTagsByFilterDialog,
  ModifyVulnerabilityTagsDialog
} from './tag-dialog'
import { PureQueryOptions } from 'apollo-client'
import { getInitialTags, TagCount } from '../../../legacy-design-system/tags'
import {
  ConfirmationRoleActionProps,
  Role
} from '@contrast-security-inc/base-types'
import { IconTag } from '@contrast-security-inc/design-system-foundations'

interface Vulnerability {
  id: string
  tags: string[]
}

interface BulkActionProps extends Pick<ConfirmationRoleActionProps, 'size'> {
  vulnerabilities: Vulnerability[]
  organizationID: string
  showDisabled?: boolean
  applicationRole: Role
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>
  bulk?: boolean
}

export function ModifyVulnerabilityTagsAction({
  vulnerabilities,
  organizationID,
  showDisabled,
  applicationRole,
  refetchQueries,
  refresh,
  bulk = false,
  size
}: BulkActionProps) {
  const tagCount =
    vulnerabilities.length === 0 ? 0 : vulnerabilities[0].tags.length
  const showTagCount = tagCount > 0 && vulnerabilities.length === 1 && !bulk
  return (
    <ConfirmationRoleAction
      size={size}
      role={applicationRole}
      isActionValid={true}
      requiredRole="EDIT"
      iconMetadata={
        showTagCount ? (
          <>
            <TagCount tagCount={tagCount} />{' '}
          </>
        ) : undefined
      }
      icon={IconTag}
      data-testid="action_tag"
      showDisabled={showDisabled}
      iconTooltipMessage={{ id: 'action_tag' }}
      disabledTooltipMessage={{
        id: 'title_vulnerabilities_insufficientTagPermissions'
      }}
      iconTooltipPlacement="top"
      renderDialog={({ isOpen, closeDialog }: RenderDialogProps) => {
        return !isOpen ? null : (
          <ModifyVulnerabilityTagsDialog
            organizationID={organizationID}
            vulnerabilityIDs={vulnerabilities.map(
              (vulnerability) => vulnerability.id
            )}
            closeDialog={closeDialog}
            isOpen={isOpen}
            initialTags={getInitialTags(vulnerabilities)}
            refetchQueries={refetchQueries}
            refresh={refresh}
          />
        )
      }}
    />
  )
}

interface BulkByFilterActionProps
  extends Pick<ConfirmationRoleActionProps, 'size'> {
  vulnerabilities: Vulnerability[]
  organizationID: string
  refetchQueries: PureQueryOptions[]
  showDisabled?
  refresh: () => Promise<any>
  excludedVulnerabilities: Vulnerability[]
  filters: {
    filterText: string
    quickFilter: string
    aggregations: Record<string, string[]>
  }
  additionalFilters?: { [key: string]: any }
}

export function ModifyVulnerabilityTagsByFilterAction({
  vulnerabilities,
  organizationID,
  excludedVulnerabilities,
  filters,
  additionalFilters,
  refetchQueries,
  showDisabled,
  refresh,
  size
}: BulkByFilterActionProps) {
  return (
    <ConfirmationAction
      size={size}
      icon={IconTag}
      iconTooltipMessage={
        showDisabled
          ? { id: 'title_vulnerabilities_insufficientTagPermissions' }
          : { id: 'action_tag' }
      }
      iconTooltipPlacement="top"
      data-testid="action_tag"
      isDisabled={showDisabled}
      renderDialog={({ isOpen, closeDialog }) => {
        return (
          <ModifyVulnerabilityTagsByFilterDialog
            organizationID={organizationID}
            excludedVulnerabilities={excludedVulnerabilities.map(
              (vulnerability) => vulnerability.id
            )}
            isOpen={isOpen}
            closeDialog={closeDialog}
            initialTags={getInitialTags(vulnerabilities)}
            filters={filters}
            additionalFilters={additionalFilters}
            refetchQueries={refetchQueries}
            refresh={refresh}
            vulnerabilityIDs={vulnerabilities.map(
              (vulnerability) => vulnerability.id
            )}
          />
        )
      }}
    />
  )
}
